import React from 'react';
import { iconDark, iconGradient, iconWhite, logoDark, logoGradient, logoMetallic, logoWhite } from 'src/assets/logos';
import { cn } from 'src/tef/components/utils/cn';

type LogoVariant = 'dark' | 'white' | 'gradient' | 'metallic' | 'auto';
type LogoType = 'full' | 'icon';
type LogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface BrandLogoProps {
  variant?: LogoVariant;
  type?: LogoType;
  size?: LogoSize;
  className?: string;
  withClearSpace?: boolean;
}

const sizeMap: Record<LogoSize, { height: string; clearY: string; clearX: string }> = {
  xs: { height: 'h-4', clearY: 'py-1', clearX: 'px-2' },
  sm: { height: 'h-6', clearY: 'py-1.5', clearX: 'px-3' },
  md: { height: 'h-10', clearY: 'py-2.5', clearX: 'px-5' },
  lg: { height: 'h-14', clearY: 'py-3.5', clearX: 'px-7' },
  xl: { height: 'h-20', clearY: 'py-5', clearX: 'px-10' },
};

const srcMap: Record<string, Record<LogoType, string>> = {
  dark: { full: logoDark, icon: iconDark },
  white: { full: logoWhite, icon: iconWhite },
  gradient: { full: logoGradient, icon: iconGradient },
  metallic: { full: logoMetallic, icon: logoMetallic },
};

const BrandLogo: React.FC<BrandLogoProps> = ({
  variant = 'auto',
  type = 'full',
  size = 'md',
  className,
  withClearSpace = true,
}) => {
  const { height, clearY, clearX } = sizeMap[size];
  const wrapperClass = cn('inline-flex items-center', withClearSpace && clearY, withClearSpace && clearX, className);

  if (variant === 'auto') {
    return (
      <span className={wrapperClass}>
        <img src={srcMap.dark[type]} alt="Tropel" className={cn(height, 'w-auto object-contain dark:hidden')} />
        <img src={srcMap.white[type]} alt="Tropel" className={cn(height, 'w-auto object-contain hidden dark:block')} />
      </span>
    );
  }

  if (variant === 'gradient') {
    return (
      <span className={wrapperClass}>
        <img
          src={srcMap.gradient[type]}
          alt="Tropel"
          className={cn(height, 'w-auto object-contain dark:hidden')}
          style={{ filter: 'grayscale(1) brightness(0.55) contrast(1.1)' }}
        />
        <img
          src={srcMap.metallic[type]}
          alt="Tropel"
          className={cn(height, 'w-auto object-contain hidden dark:block')}
        />
      </span>
    );
  }

  const resolved = srcMap[variant] || srcMap.dark;

  return (
    <span className={wrapperClass}>
      <img src={resolved[type]} alt="Tropel" className={cn(height, 'w-auto object-contain')} />
    </span>
  );
};

export default BrandLogo;
